import React, { useEffect, useState } from 'react';
import style from './ApplicationField.style';
import { Button, Form, FormGroup, Input, InputGroup, InputGroupText, Label } from 'reactstrap';
import { faBox } from '@fortawesome/free-solid-svg-icons';
import { faSquare, faSquareCheck } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DatePicker } from 'react-widgets/cjs';
import dayjs from 'dayjs';

const ApplicationField = (props) => {
  const {
    label,
    type,
    value,
    onChange,
    placeholder,
    required,
    fieldId,
    doValidate,
    disabled,
    showNA = true,
    isReviewMode,
    isHeader,
  } = props;

  const [myValue, setMyValue] = useState();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setMyValue(value);
  }, [label, value]);

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      if (windowWidth < windowHeight && windowWidth < 850) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renderInput = () => {
    // Handle header rendering for section labels
    if (isHeader) {
      return (
        <div
          style={{
            width: '100%',
            marginBottom: 15,
            marginTop: 20,
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#2e6da4',
            borderBottom: '2px solid #2e6da4',
            paddingBottom: 5,
            textAlign: 'left',
            boxSizing: 'border-box',
          }}
        >
          {label}
        </div>
      );
    }

    // Handle review mode rendering for compact "label: value" format
    if (isReviewMode) {
      const displayValue = value || '(This was left blank)';
      return (
        <div
          style={{
            width: '100%',
            marginBottom: 8,
            padding: 8,
            backgroundColor: '#f8f9fa',
            borderRadius: 4,
            border: '1px solid #e9ecef',
            fontSize: '14px',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            boxSizing: 'border-box',
          }}
        >
          <span style={{ fontWeight: 'bold', color: '#495057', textAlign: 'left', minWidth: 'fit-content' }}>
            {label}:
          </span>
          <span
            style={{
              marginLeft: 8,
              color: value ? '#212529' : '#6c757d',
              fontStyle: value ? 'normal' : 'italic',
              textAlign: 'left',
              flex: 1,
              wordWrap: 'break-word',
            }}
          >
            {displayValue}
          </span>
        </div>
      );
    }

    if (isMobile) {
      switch (type) {
        case 'text':
          return (
            <Form style={{ width: '100%' }}>
              <FormGroup>
                <Label style={{ textWrap: 'wrap', textAlign: 'left' }} for={fieldId}>
                  {' '}
                  {label} {required ? <b style={{ color: 'red' }}>*</b> : ''}
                </Label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                  <Input
                    disabled={disabled || myValue === ' '}
                    invalid={doValidate && !value}
                    id={fieldId}
                    value={myValue ? myValue : ' '}
                    type="text"
                    style={{
                      height: 'auto',
                      textWrap: 'wrap',
                      width: showNA && !required ? 'calc(100% - 60px - 8px)' : '100%',
                    }}
                    onChange={(e) => {
                      setMyValue(e.target.value);
                      onChange(e);
                    }}
                  />
                  {showNA && !required && (
                    <Button
                      size="sm"
                      color={myValue === ' ' ? 'primary' : 'secondary'}
                      disabled={disabled}
                      style={{ width: '60px', fontSize: '11px' }}
                      onClick={() => {
                        const newValue = myValue === ' ' ? null : ' ';
                        setMyValue(newValue);
                        onChange({ target: { value: newValue } });
                      }}
                    >
                      N/A
                    </Button>
                  )}
                </div>
              </FormGroup>
            </Form>
          );
        case 'textarea':
          return (
            <Form style={{ width: '100%' }}>
              <FormGroup>
                <Label style={{ width: 'auto', textWrap: 'wrap', textAlign: 'left' }}>
                  {' '}
                  {label} {required ? <b style={{ color: 'red' }}>*</b> : ''}{' '}
                </Label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', width: '100%' }}>
                  <Input
                    disabled={disabled || myValue === ' '}
                    invalid={doValidate && !value}
                    id={fieldId}
                    value={myValue ? myValue : ' '}
                    type="textarea"
                    rows={4}
                    style={{
                      height: 'auto',
                      textWrap: 'wrap',
                      width: showNA && !required ? 'calc(100% - 60px - 8px)' : '100%',
                    }}
                    onChange={(e) => {
                      setMyValue(e.target.value);
                      onChange(e);
                    }}
                  />
                  {showNA && !required && (
                    <Button
                      size="sm"
                      color={myValue === ' ' ? 'primary' : 'secondary'}
                      disabled={disabled}
                      style={{ width: '60px', fontSize: '11px' }}
                      onClick={() => {
                        const newValue = myValue === ' ' ? null : ' ';
                        setMyValue(newValue);
                        onChange({ target: { value: newValue } });
                      }}
                    >
                      N/A
                    </Button>
                  )}
                </div>
              </FormGroup>
            </Form>
          );

        case 'date':
          const formattedDate = myValue ? dayjs(myValue).format('YYYY-MM-DD') : null;

          const dateToDisplay = myValue ? new Date(formattedDate) : null;

          return (
            <Form style={{ width: '100%' }}>
              <FormGroup>
                <Label style={{ width: 'auto', textWrap: 'wrap', textAlign: 'left' }}>
                  {' '}
                  {label} {required ? <b style={{ color: 'red' }}>*</b> : ''}
                </Label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                  <div style={{ width: showNA && !required ? 'calc(100% - 60px - 8px)' : '100%' }}>
                    <DatePicker
                      value={dateToDisplay}
                      placeholder="Select a date"
                      valueFormat={{ month: 'short', year: 'numeric' }}
                      valueEditFormat={{ dateStyle: 'long' }}
                      valueDisplayFormat={{ dateStyle: 'long' }}
                      disabled={disabled || myValue === ' '}
                      style={{ width: '100%' }}
                      onToggle={() => {
                        // setShowPicker(!showPicker);
                      }}
                      onClick={(e) => {}}
                      onChange={(e) => {
                        setMyValue(e);
                        onChange({ target: { value: e } });
                      }}
                    />
                  </div>
                  {showNA && !required && (
                    <Button
                      size="sm"
                      color={myValue === ' ' ? 'primary' : 'secondary'}
                      disabled={disabled}
                      style={{ width: '60px', fontSize: '11px' }}
                      onClick={() => {
                        const newValue = myValue === ' ' ? null : ' ';
                        setMyValue(newValue);
                        onChange({ target: { value: newValue } });
                      }}
                    >
                      N/A
                    </Button>
                  )}
                </div>
              </FormGroup>
            </Form>
          );

        case 'tel':
          return (
            <Form style={{ width: '100%' }}>
              <FormGroup>
                <Label style={{ width: 'auto', textWrap: 'wrap', textAlign: 'left' }}>
                  {' '}
                  {label} {required ? <b style={{ color: 'red' }}>*</b> : ''}
                </Label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                  <Input
                    disabled={disabled || myValue === ' '}
                    invalid={doValidate && !value}
                    id={fieldId}
                    value={myValue ? myValue : ' '}
                    type="tel"
                    rows={4}
                    style={{
                      height: 'auto',
                      textWrap: 'wrap',
                      width: showNA && !required ? 'calc(100% - 60px - 8px)' : '100%',
                    }}
                    onChange={(e) => {
                      setMyValue(e.target.value);
                      onChange(e);
                    }}
                  />
                  {showNA && !required && (
                    <Button
                      size="sm"
                      color={myValue === ' ' ? 'primary' : 'secondary'}
                      disabled={disabled}
                      style={{ width: '60px', fontSize: '11px' }}
                      onClick={() => {
                        const newValue = myValue === ' ' ? null : ' ';
                        setMyValue(newValue);
                        onChange({ target: { value: newValue } });
                      }}
                    >
                      N/A
                    </Button>
                  )}
                </div>
              </FormGroup>
            </Form>
          );
        case 'checkbox':
          return (
            <Form style={{ width: '100%' }}>
              <FormGroup>
                <Label style={{ width: 200, textWrap: 'wrap', textAlign: 'left' }}>
                  {' '}
                  {label} {required ? <b style={{ color: 'red' }}>*</b> : ''}
                </Label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '15px',
                      width: '100%',
                      border: '1px solid lightgray',
                      borderRadius: 5,
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      padding: '6px 10px',
                      backgroundColor: disabled || myValue === ' ' ? '#e9ecef' : 'white',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Input
                        disabled={disabled || myValue === ' '}
                        type="radio"
                        id={`${fieldId}_yes`}
                        name={fieldId}
                        checked={myValue === true}
                        onChange={() => {
                          setMyValue(true);
                          onChange({ target: { value: true } });
                        }}
                      />
                      <Label for={`${fieldId}_yes`} style={{ margin: 0, fontSize: '14px' }}>
                        Yes
                      </Label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Input
                        disabled={disabled || myValue === ' '}
                        type="radio"
                        id={`${fieldId}_no`}
                        name={fieldId}
                        checked={myValue === false}
                        onChange={() => {
                          setMyValue(false);
                          onChange({ target: { value: false } });
                        }}
                      />
                      <Label for={`${fieldId}_no`} style={{ margin: 0, fontSize: '14px' }}>
                        No
                      </Label>
                    </div>
                  </div>
                </div>
              </FormGroup>
            </Form>
          );

        case 'switch':
          return (
            <InputGroup style={{ height: 'auto', width: '100%' }}>
              <InputGroupText style={{ width: 200, textWrap: 'wrap', textAlign: 'left' }}>
                {label}
                {required ? <b style={{ color: 'red' }}>*</b> : ''}
              </InputGroupText>

              <Input style={{ width: '0px', display: 'none' }} />

              <InputGroupText
                style={{
                  width: showNA && !required ? 'calc(100% - 200px - 60px)' : 'calc(100% - 200px)',
                  backgroundColor: 'white',
                }}
              >
                <Input
                  disabled={disabled || myValue === ' '}
                  invalid={doValidate && !value}
                  type="switch"
                  label={myValue ? 'Yes' : 'No'}
                  id={fieldId}
                  name={fieldId}
                  checked={myValue}
                  // checked={showPastInterviewDates == 'false' ? false : true}
                  onChange={(e) => {
                    setMyValue(e.target.checked);
                    onChange(e);
                  }}
                />
              </InputGroupText>

              {showNA && !required && (
                <InputGroupText style={{ width: '60px', padding: 0 }}>
                  <Button
                    size="sm"
                    color={myValue === ' ' ? 'primary' : 'secondary'}
                    disabled={disabled}
                    style={{ width: '100%', fontSize: '11px', margin: 0 }}
                    onClick={() => {
                      const newValue = myValue === ' ' ? null : ' ';
                      setMyValue(newValue);
                      onChange({ target: { value: newValue } });
                    }}
                  >
                    N/A
                  </Button>
                </InputGroupText>
              )}
            </InputGroup>
          );
        default:
          return (
            <div>
              {label}: {type}
            </div>
          );
      }
    } else {
      switch (type) {
        case 'text':
          return (
            <InputGroup style={{ height: 'auto', width: '100%' }}>
              <InputGroupText style={{ width: 200, textWrap: 'wrap', textAlign: 'left' }}>
                <span>
                  {label}
                  {required ? <b style={{ color: 'red' }}>*</b> : ''}
                </span>
              </InputGroupText>

              <Input
                bsSize="sm"
                disabled={disabled || myValue === ' '}
                invalid={doValidate && !value}
                id={fieldId}
                value={myValue ? myValue : ' '}
                type="text"
                style={{
                  height: 'auto',
                  textWrap: 'wrap',
                  width: showNA && !required ? 'calc(100% - 200px - 60px)' : 'calc(100% - 200px)',
                }}
                onChange={(e) => {
                  setMyValue(e.target.value);
                  onChange(e);
                }}
              />

              {showNA && !required && (
                <InputGroupText style={{ width: '60px', padding: 0 }}>
                  <Button
                    size="sm"
                    color={myValue === ' ' ? 'primary' : 'secondary'}
                    disabled={disabled}
                    style={{ width: '100%', fontSize: '11px', margin: 0 }}
                    onClick={() => {
                      const newValue = myValue === ' ' ? null : ' ';
                      setMyValue(newValue);
                      onChange({ target: { value: newValue } });
                    }}
                  >
                    N/A
                  </Button>
                </InputGroupText>
              )}
            </InputGroup>
          );
        case 'textarea':
          return (
            <InputGroup style={{ height: 'auto', width: '100%' }}>
              <InputGroupText style={{ width: 200, textWrap: 'wrap', textAlign: 'left' }}>
                <span>
                  {label}
                  {required ? <b style={{ color: 'red' }}>*</b> : ''}
                </span>
              </InputGroupText>

              <Input
                disabled={disabled || myValue === ' '}
                invalid={doValidate && !value}
                id={fieldId}
                value={myValue ? myValue : ' '}
                type="textarea"
                rows={4}
                style={{
                  height: 'auto',
                  textWrap: 'wrap',
                  width: showNA && !required ? 'calc(100% - 200px - 60px)' : 'calc(100% - 200px)',
                }}
                onChange={(e) => {
                  setMyValue(e.target.value);
                  onChange(e);
                }}
              />

              {showNA && !required && (
                <InputGroupText style={{ width: '60px', padding: 0, alignItems: 'flex-start', paddingTop: '2px' }}>
                  <Button
                    size="sm"
                    color={myValue === ' ' ? 'primary' : 'secondary'}
                    disabled={disabled}
                    style={{ width: '100%', fontSize: '11px', margin: 0 }}
                    onClick={() => {
                      const newValue = myValue === ' ' ? null : ' ';
                      setMyValue(newValue);
                      onChange({ target: { value: newValue } });
                    }}
                  >
                    N/A
                  </Button>
                </InputGroupText>
              )}
            </InputGroup>
          );
        case 'date':
          const formattedDate = myValue ? dayjs(myValue).format('YYYY-MM-DD') : null;

          const dateToDisplay = myValue ? new Date(formattedDate) : null;

          return (
            <InputGroup style={{ height: 'auto', width: '100%' }}>
              <InputGroupText style={{ width: 200, textWrap: 'wrap', textAlign: 'left' }}>
                <span>
                  {label}
                  {required ? <b style={{ color: 'red' }}>*</b> : ''}
                </span>
              </InputGroupText>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: showNA && !required ? 'calc(100% - 200px - 60px)' : 'calc(100% - 200px)',
                  border: '1px solid lightgray',
                  borderLeft: 'none',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  backgroundColor: disabled ? '#e9ecef' : 'white',
                  // borderRadius: '0 30px 30px 0',
                }}
              >
                <DatePicker
                  disabled={disabled || myValue === ' '}
                  value={dateToDisplay}
                  placeholder="Select a date"
                  valueFormat={{ month: 'short', year: 'numeric' }}
                  valueEditFormat={{ dateStyle: 'long' }}
                  valueDisplayFormat={{ dateStyle: 'long' }}
                  style={{ width: '100%' }}
                  onToggle={() => {
                    // setShowPicker(!showPicker);
                  }}
                  onClick={(e) => {}}
                  onChange={(e) => {
                    setMyValue(e);
                    onChange({ target: { value: e } });
                  }}
                />
              </div>

              {showNA && !required && (
                <InputGroupText style={{ width: '60px', padding: 0 }}>
                  <Button
                    size="sm"
                    color={myValue === ' ' ? 'primary' : 'secondary'}
                    disabled={disabled}
                    style={{ width: '100%', fontSize: '11px', margin: 0 }}
                    onClick={() => {
                      const newValue = myValue === ' ' ? null : ' ';
                      setMyValue(newValue);
                      onChange({ target: { value: newValue } });
                    }}
                  >
                    N/A
                  </Button>
                </InputGroupText>
              )}
            </InputGroup>
          );
        case 'tel':
          return (
            <InputGroup style={{ height: 'auto', width: '100%' }}>
              <InputGroupText style={{ width: 200, textWrap: 'wrap', textAlign: 'left' }}>
                <span>
                  {label}
                  {required ? <b style={{ color: 'red' }}>*</b> : ''}
                  {`(${type})`}
                </span>
              </InputGroupText>

              <Input
                disabled={disabled || myValue === ' '}
                invalid={doValidate && !value}
                id={fieldId}
                value={myValue ? myValue : ' '}
                type="tel"
                style={{
                  height: 'auto',
                  textWrap: 'wrap',
                  width: showNA && !required ? 'calc(100% - 200px - 60px)' : 'calc(100% - 200px)',
                }}
                onChange={(e) => {
                  setMyValue(e.target.value);
                  onChange(e);
                }}
              />

              {showNA && !required && (
                <InputGroupText style={{ width: '60px', padding: 0 }}>
                  <Button
                    size="sm"
                    color={myValue === ' ' ? 'primary' : 'secondary'}
                    disabled={disabled}
                    style={{ width: '100%', fontSize: '11px', margin: 0 }}
                    onClick={() => {
                      const newValue = myValue === ' ' ? null : ' ';
                      setMyValue(newValue);
                      onChange({ target: { value: newValue } });
                    }}
                  >
                    N/A
                  </Button>
                </InputGroupText>
              )}
            </InputGroup>
          );
        case 'checkbox':
          return (
            <InputGroup style={{ height: 'auto', width: '100%' }}>
              <InputGroupText style={{ width: 200, textWrap: 'wrap', textAlign: 'left' }}>
                <span>
                  {label}
                  {required ? <b style={{ color: 'red' }}>*</b> : ''}
                </span>
              </InputGroupText>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '15px',
                  width: 'calc(100% - 200px)',
                  border: '1px solid lightgray',
                  borderLeft: 'none',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  padding: '6px 10px',
                  backgroundColor: disabled || myValue === ' ' ? '#e9ecef' : 'white',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Input
                    disabled={disabled || myValue === ' '}
                    type="radio"
                    id={`${fieldId}_yes`}
                    name={fieldId}
                    checked={myValue === true}
                    onChange={() => {
                      setMyValue(true);
                      onChange({ target: { value: true } });
                    }}
                  />
                  <Label for={`${fieldId}_yes`} style={{ margin: 0, fontSize: '14px' }}>
                    Yes
                  </Label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Input
                    disabled={disabled || myValue === ' '}
                    type="radio"
                    id={`${fieldId}_no`}
                    name={fieldId}
                    checked={myValue === false}
                    onChange={() => {
                      setMyValue(false);
                      onChange({ target: { value: false } });
                    }}
                  />
                  <Label for={`${fieldId}_no`} style={{ margin: 0, fontSize: '14px' }}>
                    No
                  </Label>
                </div>
              </div>
            </InputGroup>
          );
        case 'switch':
          return (
            <InputGroup style={{ height: 'auto', width: '100%' }}>
              <InputGroupText style={{ width: 200, textWrap: 'wrap', textAlign: 'left' }}>
                {label}
                {required ? <b style={{ color: 'red' }}>*</b> : ''}
              </InputGroupText>

              <Input style={{ width: '0px', display: 'none' }} />

              <InputGroupText
                style={{
                  width: showNA && !required ? 'calc(100% - 200px - 60px)' : 'calc(100% - 200px)',
                  backgroundColor: 'white',
                }}
              >
                <Input
                  disabled={disabled || myValue === ' '}
                  invalid={doValidate && !value}
                  type="switch"
                  label={myValue ? 'Yes' : 'No'}
                  id={fieldId}
                  name={fieldId}
                  checked={myValue}
                  // checked={showPastInterviewDates == 'false' ? false : true}
                  onChange={(e) => {
                    setMyValue(e.target.checked);
                    onChange(e);
                  }}
                />
              </InputGroupText>

              {showNA && !required && (
                <InputGroupText style={{ width: '60px', padding: 0 }}>
                  <Button
                    size="sm"
                    color={myValue === ' ' ? 'primary' : 'secondary'}
                    disabled={disabled}
                    style={{ width: '100%', fontSize: '11px', margin: 0 }}
                    onClick={() => {
                      const newValue = myValue === ' ' ? null : ' ';
                      setMyValue(newValue);
                      onChange({ target: { value: newValue } });
                    }}
                  >
                    N/A
                  </Button>
                </InputGroupText>
              )}
            </InputGroup>
          );
        default:
          return (
            <div>
              {label}: {type}
            </div>
          );
      }
    }
  };
  return (
    <div
      key={fieldId}
      style={{ ...style.simpleRow, marginBottom: 5, width: '100%', paddingLeft: 10, paddingRight: 10 }}
    >
      {renderInput()}
    </div>
  );
};

export default ApplicationField;
